import { ProxyAbi } from '../../abi/abi'
import Web3 from 'web3'
import { Address, Wei, SmartContractAction } from '../../utils/types'
import { Token } from '../../logic/token/token'
import { TokenApi } from './token-api'
import { maxDepositSlippage } from '../../config'
import { maxApprove, maxDeadline } from '../../utils/static-web3'
import { PoolWithData, TokenWithData, ZeroExQuote } from '../api-types'

import { DepositResult } from './deposit-api'
import { moreGas } from '../../logic/weis/wei-calculate'
import { calculateAfterOneAssetJoin } from '../../logic/financials/calculate-after-one-asset-join'
import { PoolApi } from './pool-api'
import { Swap as SwapObject, Swap } from '../../logic/swaps/Swap'
import { multiply } from '../../logic/weis/wei-calculate'

import { extractErrorMessage } from '../../logic/errors/extract-error-message'
import { Hash, TransactionAction } from '../../logic/transactions/transactions'
import { Dispatch } from 'react'
import { Amounts } from '../../logic/token/amounts'
import { oracleApi } from '../oracle/oracle-client'

export class ProxyApi {
  // @ts-ignore
  contract

  constructor(
    public web3: Web3,
    public account: Address,
    public chainId: number,
    public proxyAddress: Address
  ) {
    // @ts-ignore
    this.contract = new web3.eth.Contract(ProxyAbi, this.proxyAddress)
  }

  async approveProxy(token: Token) {
    const tokenApi = new TokenApi(this.web3, this.account, token)
    await tokenApi.allow(this.proxyAddress, maxApprove)
  }

  async swapIn(
    swap: Swap,
    onTransactionPending: (txHash: Hash, swap: SwapObject) => void,
    onTransactionSuccess: (txHash: Hash, swap: SwapObject) => void,
    onTransactionError: (txHash: Hash | undefined, errorMessage: string) => void
  ) {
    const { pool, tokenIn, tokenOut, inAmountWei } = swap
    const minAmountOutWei = swap.calculateMinAmountWei()
    const maxPrice = swap.getMaxPrice()
    const swapData = [
      pool,
      tokenIn.address,
      tokenOut.address,
      inAmountWei,
      minAmountOutWei,
      maxPrice,
    ]

    const gas = await this.contract.methods
      .batchSwapExactIn(
        [swapData],
        tokenIn.address,
        tokenOut.address,
        inAmountWei,
        minAmountOutWei,
        maxDeadline
      )
      .estimateGas({ from: this.account })

    let resolved = false
    return new Promise(async (resolve, reject) => {
      try {
        await this.contract.methods
          .batchSwapExactIn(
            [swapData],
            tokenIn.address,
            tokenOut.address,
            inAmountWei,
            minAmountOutWei,
            maxDeadline
          )
          .send({
            from: this.account,
            gas: moreGas(gas, SmartContractAction.SWAP_GIVEN_IN),
            maxPriorityFeePerGas: null,
            maxFeePerGas: null,
          })
          .once('transactionHash', (hash: any) => {
            swap.transactionHash = hash
            onTransactionPending(hash, swap)
            resolve({ hash, swap })
            resolved = true
          })
          .once('receipt', (receipt: any) => {
            const exactOutWei = this.getAmountTransferred(receipt.events, 3)
            swap.setRealAmountWei(exactOutWei)
            //swap.setTransactionHash(receipt.transactionHash)
            onTransactionSuccess(receipt.transactionHash, swap)
          })
          .once('error', (error: any) => {
            if (!resolved) {
              // when rejected, onTransactionError will be called
              reject(error)
            } else {
              // we have the hash, transaction was submitted
              onTransactionError(
                swap.transactionHash,
                extractErrorMessage(error)
              )
            }
          })
      } catch (e: any) {
        if (!resolved) {
          // when rejected, onTransactionError will be called
          reject(e)
        } else {
          console.warn(
            'Error handled after tx accepted by node. SHould be caught by onTransactionError. Illegal state ',
            extractErrorMessage(e)
          )
        }
      }
    })
  }

  calculateMaxAmountsIn(tokens: Token[], amounts: Map<Token, Wei>): Wei[] {
    // get tokens information
    const numTokens = tokens.length
    let maxAmountsIn = new Array(numTokens)
    for (let i = 0; i < numTokens; i++) {
      maxAmountsIn[i] = multiply(
        amounts.get(tokens[i])!,
        1 + maxDepositSlippage
      )
    }

    return maxAmountsIn
  }

  async isAllowed(token: Token, amount: Wei) {
    const tokenApi = new TokenApi(this.web3, this.account, token)
    const allowed = await tokenApi.allowance(this.proxyAddress)
    return Number(allowed) >= Number(amount)
  }

  async allowedTokensForMultiJoin(
    pool: PoolWithData,
    amounts: Map<TokenWithData, Wei>
  ) {
    const tokens = pool.tokens

    const maxAmountsIn = this.calculateMaxAmountsIn(tokens, amounts)

    // now check if allowance is needed
    const poolApi = new PoolApi(this.web3, this.account, pool.address)
    const allowed = await poolApi.allowance(pool, this.proxyAddress)
    const tokensAllowed: Token[] = tokens.filter((t, i) => {
      return Number(allowed.get(t)!) >= Number(maxAmountsIn[i])
    })

    return tokensAllowed
  }

  /**
   * This function supposes tokens are all allowed
   * @param pool
   * @param poolAmountOut
   * @param amounts
   * @param dispatch
   * @param onTransactionError
   */
  async depositMulti(
    pool: PoolWithData,
    poolAmountOut: Wei,
    amounts: Map<TokenWithData, Wei>,
    dispatch: Dispatch<TransactionAction>
  ): Promise<DepositResult> {
    const maxAmountsIn: Wei[] = []

    for (const t of pool.tokens) {
      maxAmountsIn.push(amounts.get(t)!)
    }

    const deadline = await this.getDeadline()

    const gas = await this.contract.methods
      .joinPool(pool.address, poolAmountOut, maxAmountsIn, deadline)
      .estimateGas({ from: this.account })

    let txHash: string | undefined = undefined

    try {
      const result = await this.contract.methods
        .joinPool(pool.address, poolAmountOut, maxAmountsIn, deadline)
        .send({
          from: this.account,
          gas: moreGas(gas, SmartContractAction.MULTI_ASSET_JOIN),
          maxPriorityFeePerGas: null,
          maxFeePerGas: null,
        })
        .once('transactionHash', (hash: any) => {
          txHash = hash
          dispatch({
            type: 'submit',
            tx: {
              nonce: -1,
              type: 'deposit-multi',
              status: 'pending',
              chainId: this.chainId,
              account: this.account,
              submitted: {
                hash,
                speedUp: false,
                start: new Date().getTime(),
              },
              data: {
                amounts: Object.fromEntries(amounts),
                pool,
              },
              deposit: { amounts, pool },
            },
          })
        })
      // we have succcess
      dispatch({
        type: 'complete',
        hash: txHash,
      })

      const { symbol, name } = await TokenApi.getTokenInfo(
        this.web3,
        pool.address
      )
      return {
        address: pool.address,
        name: name,
        symbol: symbol,
        // TODO: just expected value here
        value: poolAmountOut,
        amounts,
        pool,
        hash: txHash!,
        bringIn: Amounts.fromMap(amounts),
        mode: 'all-asset',
      }
    } catch (error: any) {
      dispatch({
        type: 'fail',
        hash: txHash,
      })
      throw {
        hash: txHash,
        message: extractErrorMessage(error),
      }
    }
  }

  async depositOneAssetViaZeroEx(
    pool: PoolWithData,
    fillQuotes: ZeroExQuote[],
    joiningAsset: Token,
    joiningAssetAmount: Wei,
    poolAmountOut: Wei,
    amounts: Map<Token, Wei>,
    dispatch: Dispatch<TransactionAction>
  ): Promise<DepositResult> {
    const maxAmountsIn = this.calculateMaxAmountsIn(pool.tokens, amounts)

    const isNative = new TokenApi(
      this.web3,
      this.account,
      joiningAsset
    ).isNative()

    const deadline = await this.getDeadline()

    const formatedFillQuotes = fillQuotes.map(q => ({
      buyToken: q.buyTokenAddress,
      sellAmount: q.sellAmount,
      guaranteedAmountOut: q.guaranteedBuyAmount,
      spender: q.spender,
      swapCallData: q.swapCallData,
    }))

    const { symbol, name } = await TokenApi.getTokenInfo(
      this.web3,
      pool.address
    )

    const gas = await this.contract.methods
      .joinPoolVia0x(
        pool.tokens.map(t => t.address),
        maxAmountsIn,
        formatedFillQuotes,
        joiningAsset.address,
        joiningAssetAmount,
        pool.address,
        poolAmountOut,
        deadline
      )
      .estimateGas({
        from: this.account,
        value: isNative ? joiningAssetAmount : '0',
      })

    let txHash: string | undefined = undefined

    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.contract.methods
          .joinPoolVia0x(
            pool.tokens.map(t => t.address),
            maxAmountsIn,
            formatedFillQuotes,
            joiningAsset.address,
            joiningAssetAmount,
            pool.address,
            poolAmountOut,
            deadline
          )
          .send({
            from: this.account,
            gas: moreGas(gas, SmartContractAction.ONE_ASSET_JOIN_VIA_ZEROX),
            maxPriorityFeePerGas: null,
            maxFeePerGas: null,
            value: isNative ? joiningAssetAmount : '0',
          })
          .once('transactionHash', (hash: any) => {
            txHash = hash
            dispatch({
              type: 'submit',
              tx: {
                nonce: -1,
                type: 'deposit-one',
                status: 'pending',
                chainId: this.chainId,
                account: this.account,
                submitted: {
                  hash,
                  speedUp: false,
                  start: new Date().getTime(),
                },
                data: {
                  token: joiningAsset,
                  amount: joiningAssetAmount,
                  pool,
                },
                deposit: {
                  token: joiningAsset,
                  amount: joiningAssetAmount,
                  pool,
                },
              },
            })
          })
          .once('receipt', (receipt: any) => {
            // we have succcess
            dispatch({
              type: 'complete',
              hash: txHash,
            })

            resolve({
              address: pool.address,
              name: name,
              symbol: symbol,
              // TODO: just expected value here
              value: poolAmountOut,
              amounts,
              pool,
              hash: txHash!,
              bringIn: new Amounts().add(joiningAsset, joiningAssetAmount),
              mode: 'aggregator',
            })
          })
          .once('error', (error: any) => {
            reject(error)
          })
      } catch (error: any) {
        dispatch({
          type: 'fail',
          hash: txHash,
        })
        console.log('got error', error, extractErrorMessage(error))

        throw error
      }
    })
  }

  async getDeadline(): Promise<string> {
    const currentBlock = await this.web3.eth.getBlockNumber()
    const timestamp = Number(
      (await this.web3.eth.getBlock(currentBlock)).timestamp
    )

    return (timestamp + 30 * 60).toString()
  }

  async allowOneAsset(pool: PoolWithData, amount: Wei, token: Token) {
    const tokenApi = new TokenApi(this.web3, this.account, token)
    await tokenApi.allow(this.proxyAddress, amount)
  }

  async depositWithOneAsset(
    pool: PoolWithData,
    amount: Wei,
    minPoolAmountOut: Wei,
    token: Token,
    dispatch: Dispatch<TransactionAction>
  ): Promise<DepositResult> {
    const web3 = this.web3
    const account = this.account

    const deadline = await this.getDeadline()
    let gasEstimate = await this.contract.methods
      .joinswapExternAmountIn(
        pool.address,
        token.address,
        amount,
        minPoolAmountOut,
        deadline
      )
      .estimateGas({ from: account })

    gasEstimate = moreGas(gasEstimate, SmartContractAction.ONE_ASSET_JOIN)

    let txHash: string | undefined = undefined

    try {
      const result = await this.contract.methods
        .joinswapExternAmountIn(
          pool.address,
          token.address,
          amount,
          minPoolAmountOut,
          deadline
        )
        .send({
          from: account,
          gas: gasEstimate,
          maxPriorityFeePerGas: null,
          maxFeePerGas: null,
        })
        .once('transactionHash', (hash: any) => {
          txHash = hash
          dispatch({
            type: 'submit',
            tx: {
              nonce: -1,
              type: 'deposit-one',
              status: 'pending',
              chainId: this.chainId,
              account: this.account,
              submitted: {
                hash,
                speedUp: false,
                start: new Date().getTime(),
              },
              data: {
                token,
                amount,
                pool,
              },
              deposit: { amount, pool },
            },
          })
        })
      dispatch({
        type: 'complete',
        hash: txHash,
      })
      const keccakTransfer = web3.utils.keccak256(
        'Transfer(address,address,uint256)'
      )

      const events = Object.values(result.events).filter(
        (e: any) => e.raw.topics[0] === keccakTransfer
      ) as any[]

      const lpContractAddress = web3.eth.abi.decodeParameter(
        'address',
        events[2].raw.topics[2]
      ) as any as Address

      const amountTransferred = web3.utils.hexToNumberString(events[2].raw.data)

      const { symbol, name, address } = await TokenApi.getTokenInfo(
        web3,
        lpContractAddress
      )

      const map = calculateAfterOneAssetJoin(
        amount,
        token,
        amountTransferred,
        pool
      )

      const deposit: DepositResult = {
        address,
        name: name || 'SWAAP-LP-' + pool.tokens.map(t => t.name).join('-'),
        symbol: symbol || 'SwLP-' + pool.tokens.map(t => t.symbol).join('-'),
        value: amountTransferred,
        amounts: map,
        pool,
        hash: txHash!,
        bringIn: new Amounts().add(token, amount),
        mode: '1-asset',
      }
      return deposit
    } catch (error: any) {
      dispatch({
        type: 'fail',
        hash: txHash,
      })
      // It's up to the front to handle the extract error message at last moment
      throw error
    }
  }

  /**
   *
   * @param allEvents
   * @param transferIndex as seen on Transfer in explorer main page
   */
  getAmountTransferred(allEvents: any, transferIndex: number): Wei {
    const keccakTransfer = this.web3.utils.keccak256(
      'Transfer(address,address,uint256)'
    )

    const transferEvents = Object.values(allEvents).filter(
      (e: any) => e.raw.topics[0] === keccakTransfer
    ) as any[]

    const amountTransferred = this.web3.utils.hexToNumberString(
      transferEvents[transferIndex].raw.data
    )
    return amountTransferred
  }
}
